import { z } from 'zod';
import type { IsoDate } from './common.js';
import type { UserSummary } from './users.js';

/* ---------- Releases (desktop app versions) ---------- */

export const releaseChannels = ['stable', 'beta'] as const;
export type ReleaseChannel = (typeof releaseChannels)[number];

export const assetOses = ['linux', 'windows', 'macos'] as const;
export type AssetOs = (typeof assetOses)[number];

export const assetArches = ['x86_64', 'aarch64', 'universal'] as const;
export type AssetArch = (typeof assetArches)[number];

/** Installer formats plus store listings (`store`). */
export const assetKinds = [
  'appimage',
  'deb',
  'rpm',
  'snap',
  'msi',
  'exe',
  'dmg',
  'app-tar-gz',
  'store',
] as const;
export type AssetKind = (typeof assetKinds)[number];

export const assetKindLabels: Record<AssetKind, string> = {
  appimage: 'AppImage',
  deb: '.deb (Ubuntu/Debian)',
  rpm: '.rpm (Fedora)',
  snap: 'Snap',
  msi: 'Instalador .msi',
  exe: 'Instalador .exe',
  dmg: '.dmg',
  'app-tar-gz': '.app.tar.gz (actualizador)',
  store: 'Tienda',
};

export const releaseAssetSchema = z.object({
  os: z.enum(assetOses),
  arch: z.enum(assetArches),
  kind: z.enum(assetKinds),
  /** Download or store URL. */
  url: z.url().max(2000),
  /** Human label for store links, e.g. "Snap Store". */
  label: z.string().trim().max(60).nullable().optional(),
  /** Tauri updater signature (contents of the .sig file). */
  signature: z.string().trim().max(5000).nullable().optional(),
  sizeBytes: z.number().int().positive().nullable().optional(),
});
export type ReleaseAssetInput = z.infer<typeof releaseAssetSchema>;

/** Semantic version without leading "v" (1.2.3 or 1.2.3-beta.1). */
export const versionSchema = z
  .string()
  .trim()
  .regex(/^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/, 'Versión con formato 1.2.3');

export const saveReleaseSchema = z.object({
  version: versionSchema,
  channel: z.enum(releaseChannels).default('stable'),
  notes: z.string().trim().max(10_000).default(''),
  mandatory: z.boolean().default(false),
  published: z.boolean().default(false),
  assets: z.array(releaseAssetSchema).max(30).default([]),
});
export type SaveReleaseInput = z.input<typeof saveReleaseSchema>;

export interface ReleaseAsset extends ReleaseAssetInput {
  id: string;
  label: string | null;
  signature: string | null;
  sizeBytes: number | null;
}

export interface Release {
  id: string;
  version: string;
  channel: ReleaseChannel;
  notes: string;
  mandatory: boolean;
  publishedAt: IsoDate | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
  assets: ReleaseAsset[];
}

/** A published version as people see it in «Novedades» (no installers). */
export interface ChangelogEntry {
  version: string;
  notes: string;
  publishedAt: IsoDate;
}

/** The user has read the news up to this version. */
export const changelogSeenSchema = z.object({ version: versionSchema });
export type ChangelogSeenInput = z.infer<typeof changelogSeenSchema>;

/** Team of a user, as platform admins see it. */
export interface AdminUserTeam {
  id: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
  /** Custom roles the user has in that team. */
  customRoles: string[];
  memberCount: number;
  joinedAt: IsoDate;
}

/** Member of a team, as platform admins see it. */
export interface AdminTeamMember extends UserSummary {
  email: string;
  role: 'owner' | 'admin' | 'member';
  customRoles: string[];
  joinedAt: IsoDate;
}

/** A conversation of a team, as the (temporary) admin inspection lists it. */
export interface AdminChannel {
  id: string;
  kind: 'channel' | 'dm';
  mode: 'text' | 'voice';
  /** Channel name, or the two people of a DM («Ana ↔ Bruno»). */
  name: string;
  description: string | null;
  isPrivate: boolean;
  messageCount: number;
  lastMessageAt: IsoDate | null;
}

/** A team opened from /admin (read-only, temporary testing-phase feature). */
export interface AdminTeamOverview {
  id: string;
  name: string;
  taskKey: string;
  createdAt: IsoDate;
  channels: AdminChannel[];
}

/** Compares two semantic versions. Pre-releases sort before the final version. */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) => {
    const [core = '', pre] = v.replace(/^v/, '').split('-', 2);
    return { parts: core.split('.').map((n) => Number(n) || 0), pre };
  };
  const x = parse(a);
  const y = parse(b);
  for (let i = 0; i < 3; i++) {
    const diff = (x.parts[i] ?? 0) - (y.parts[i] ?? 0);
    if (diff !== 0) return Math.sign(diff);
  }
  if (x.pre === y.pre) return 0;
  if (!x.pre) return 1;
  if (!y.pre) return -1;
  return x.pre.localeCompare(y.pre, undefined, { numeric: true });
}

/* ---------- Admin dashboard ---------- */

export interface AdminStats {
  users: { total: number; active7d: number; newThisWeek: number; suspended: number };
  teams: { total: number };
  messages: { total: number; last24h: number };
  tasks: { total: number; open: number; done: number };
  files: { count: number; bytes: number };
  webhooks: { total: number };
  /** New users and messages per day, last 14 days (oldest first). */
  daily: { date: string; users: number; messages: number }[];
}

export interface AdminUser extends UserSummary {
  email: string;
  isPlatformAdmin: boolean;
  suspendedAt: IsoDate | null;
  createdAt: IsoDate;
  lastSeenAt: IsoDate | null;
  teamCount: number;
  messageCount: number;
}

export const adminUpdateUserSchema = z
  .object({ suspended: z.boolean(), isPlatformAdmin: z.boolean() })
  .partial();
export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserSchema>;

export interface AdminTeam {
  id: string;
  name: string;
  createdAt: IsoDate;
  owner: UserSummary | null;
  memberCount: number;
  channelCount: number;
  messageCount: number;
  taskCount: number;
  lastActivityAt: IsoDate | null;
  isPublic: boolean;
  official: boolean;
  featured: boolean;
}

export const adminListQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
});
export type AdminListQuery = { q?: string; page?: number };

export interface AdminPage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AuditEntry {
  id: string;
  actor: UserSummary | null;
  action: string;
  targetType: string;
  targetId: string | null;
  summary: string;
  createdAt: IsoDate;
}

export interface SystemStatus {
  version: string;
  environment: string;
  uptimeSeconds: number;
  checks: { name: string; ok: boolean; detail: string }[];
}

/** People with Nexo open right now (heartbeats from the last minute or so). */
export interface OnlineStats {
  at: IsoDate;
  /** Signed in, by client. A person with several apps open counts once in `users`. */
  users: number;
  web: number;
  desktop: number;
  /** Without an account: website visitors and meeting guests (one per open tab). */
  visitors: number;
  /** Samples of the requested range, oldest first (the peak of each bucket). */
  history: { at: IsoDate; users: number; visitors: number }[];
  rangeMinutes: number;
  /** Minutes each history point covers (1 for the last hour). */
  bucketMinutes: number;
}

export const onlineRanges = [60, 360, 1440] as const;
export const onlineQuerySchema = z.object({
  minutes: z.coerce
    .number()
    .refine((v) => (onlineRanges as readonly number[]).includes(v))
    .default(60),
});

/** Anonymous heartbeat: a random id kept in memory by the page (nothing stored on the device). */
export const visitorHeartbeatSchema = z.object({ id: z.uuid() });
export type VisitorHeartbeatInput = z.infer<typeof visitorHeartbeatSchema>;

/* ---------- Platform settings (features the Nexo team turns on and off) ---------- */

export const platformSettingsSchema = z.object({
  /** «Explorar servidores»: the directory of public teams. */
  publicDirectory: z.boolean(),
  /** AI summaries of calls (transcript, summary and suggested tasks). */
  callSummaries: z.boolean(),
});
export type PlatformSettings = z.infer<typeof platformSettingsSchema>;
export const updatePlatformSettingsSchema = platformSettingsSchema.partial();
export type UpdatePlatformSettingsInput = z.infer<typeof updatePlatformSettingsSchema>;

/** What the apps need to know about the platform (features on/off, the official server). */
export interface PlatformFeatures extends PlatformSettings {
  officialTeamId: string | null;
  /** Call summaries are switched on and the server has the AI keys they need. */
  callSummariesReady: boolean;
}

const officialTeamName = 'Nexo Hub';
export const createOfficialTeamSchema = z.object({
  name: z.string().trim().min(2).max(50).default(officialTeamName),
  /** Also add every existing account (new ones always join). */
  addEveryone: z.boolean().default(true),
});
export type CreateOfficialTeamInput = z.input<typeof createOfficialTeamSchema>;

export const featureTeamSchema = z.object({ featured: z.boolean() });
export type FeatureTeamInput = z.infer<typeof featureTeamSchema>;
